import FormLabel from '@rio-cloud/rio-uikit/FormLabel';
import type { Control } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { ControlledNumberFormInput } from '#components/form/FormInput.tsx';
import { CapacityWarning } from '#components/tourStops/CapacityWarning.tsx';
import type { Order } from '#domain/Order.ts';
import { type CapacityMetric, getRelevantCapacityMetrics } from '#domain/tourCapacity.ts';

import type { TourFormValues } from '../model/NewTourFormState.ts';

type TourCapacityFormProps = {
    control: Control<TourFormValues>;
    orders: Order[];
    compact?: boolean;
    exceededDimensions?: CapacityMetric[];
};

export const TourCapacityForm = ({
    control,
    orders,
    compact = false,
    exceededDimensions = [],
}: TourCapacityFormProps) => {
    const intl = useIntl();
    const relevantMetrics = getRelevantCapacityMetrics(orders);
    const inputLayout = compact
        ? {
              formGroupClassName: 'tour-capacity-field margin-0 min-width-0',
              inputContainerClassName: 'tour-capacity-input flex-0',
          }
        : {};

    if (relevantMetrics.size === 0) {
        return null;
    }

    const label = (suffix: string) =>
        intl.formatMessage({
            id: `intl-msg:ordercom-cloud-frontend.tours.create-dialog.capacity.${suffix}`,
        });

    const inputLabel = (suffix: string) => {
        const fullLabel = label(suffix);
        const compactId = `intl-msg:ordercom-cloud-frontend.tours.create-dialog.capacity.${suffix}-compact`;
        return {
            label: compact && intl.messages[compactId] ? intl.formatMessage({ id: compactId }) : fullLabel,
            accessibleLabel: fullLabel,
            title: fullLabel,
        };
    };

    return (
        <section
            className={compact ? 'form-group margin-0 min-width-0' : 'margin-top-20'}
            aria-labelledby='tour-capacity-title'
        >
            {compact ? (
                <FormLabel id='tour-capacity-title'>
                    <span className='display-inline-flex align-items-center gap-5'>
                        {label('total')}
                        {exceededDimensions.length > 0 && <CapacityWarning dimensions={exceededDimensions} />}
                    </span>
                </FormLabel>
            ) : (
                <h4 id='tour-capacity-title' className='margin-top-0 margin-bottom-10'>
                    {label('title')}
                </h4>
            )}
            {!compact && exceededDimensions.length > 0 && <CapacityWarning dimensions={exceededDimensions} />}
            <div
                className={
                    compact
                        ? 'display-flex flex-wrap gap-10'
                        : 'display-grid grid-cols-1 grid-cols-2-sm grid-cols-4-lg gap-20'
                }
            >
                {relevantMetrics.has('weight') && (
                    <ControlledNumberFormInput
                        {...inputLayout}
                        control={control}
                        name='maxWeightT'
                        {...inputLabel('max-weight')}
                        addon='t'
                        min={0}
                    />
                )}
                {relevantMetrics.has('liter') && (
                    <ControlledNumberFormInput
                        {...inputLayout}
                        control={control}
                        name='maxVolumeLiter'
                        {...inputLabel('max-volume-liter')}
                        addon='l'
                        min={0}
                    />
                )}
                {relevantMetrics.has('cubicMeter') && (
                    <ControlledNumberFormInput
                        {...inputLayout}
                        control={control}
                        name='maxVolumeCubicMeter'
                        {...inputLabel('max-volume-cubic-meter')}
                        addon='m³'
                        min={0}
                    />
                )}
                {relevantMetrics.has('loadingMeter') && (
                    <ControlledNumberFormInput
                        {...inputLayout}
                        control={control}
                        name='maxLoadingMeter'
                        {...inputLabel('max-loading-meter')}
                        addon='Ldm'
                        min={0}
                    />
                )}
            </div>
        </section>
    );
};
